import { c as _c } from "react/compiler-runtime";
import { BREAKPOINTS } from '@hookdeck/theme';
import { useContext } from 'react';
import styled, { useTheme } from 'styled-components';
import { APIDestination, DestinationConfig, HTTPDestinationTypeConfig } from '../../../../../../../../typings/Destination.interface';
import { DestinationTypeFeatureTypesUnmanaged } from '../../../../../../../../typings/Integration.interface';
import { stringifyQuery } from '../../../../../utils';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import Icon, { IconName } from '../../../../common/base/Icon';
import Skeleton from '../../../../common/base/Skeleton';
import Text from '../../../../common/base/Text';
import { Div } from '../../../../common/helpers/StyledUtils';
import BaseMetricChart from '../../../../common/metrics/MetricChart';
import ChartInteractionGroup from '../../../../common/Chart/ChartInteractionGroup';
import { default_data_formatter } from '../../../../common/metrics/metric-definitions';
import { ChartDataSet } from '../../../../common/Chart/Chart';
import { DashboardContext } from '../../DashboardContext';
import useViewportBreakpoint from '../../../../hooks/useViewportBreakpoint';
import { StyledViewSplitPaneContent, StyledViewSplitPaneLayout, StyledViewSplitPaneSidebar, StyledViewSplitPaneSidebarBody, ViewMetadataCopyable, ViewMetadataDate, ViewMetadataDescription, ViewMetadataItem, ViewSplitPaneContentHeader, ViewSplitPaneSidebarHeader, ViewSplitPaneSidebarSection } from '../../../../layouts/ViewLayout';
import MetricChart from '../../Metrics/MetricChart';
import MetricDatePicker, { getDateRangeQueryParam, useDateRange } from '../../Metrics/MetricDatePicker';
import { useRefreshKey } from '../../Metrics/RefreshKeyContext';
import { ChartData, MetricConfigs, useMetric } from '../../Metrics/useMetric';
import { getDestinationDeliveryPolicy } from '../Forms/destination_configuration';
const isHttpConfig = (config: DestinationConfig): config is HTTPDestinationTypeConfig => {
  return Object.prototype.hasOwnProperty.call(config, 'delivery_policy') || Object.prototype.hasOwnProperty.call(config, 'rate_limit_period');
};
const StyledOverviewMetricsGrid = styled(Div)`
  @media (max-width: 1400px) {
    grid-template-columns: minmax(0, 1fr) !important;
  }
`;
const overview_columns_for_breakpoints: Record<keyof typeof BREAKPOINTS, number> = {
  mobile: 1,
  tablet: 1,
  laptop: 2,
  desktop: 2
};
const formatLatency = (value: number | string) => `${Math.round(Number(value)).toLocaleString()} ms`;
const event_status_configs = [{
  value: 'SUCCESSFUL',
  theme_color: 'success',
  label: 'Successful'
}, {
  value: 'FAILED',
  theme_color: 'danger',
  label: 'Failed'
}, {
  value: 'QUEUED',
  theme_color: 'primary',
  label: 'Pending'
}, {
  value: 'HOLD',
  color: 'warning',
  label: 'Paused'
}, {
  value: 'SCHEDULED',
  color: 'warning',
  label: 'Delayed'
}, {
  value: 'CANCELLED',
  color: 'neutral_histogram',
  label: 'Cancelled'
}] as const;
const formatMetadataLabel = (label: string) => label.replace(/ \(Default\)$/, '');
const toDataset = (data: ChartData | null | undefined, config: {
  key: string;
  label: string;
  sort_order?: number;
  metric_type: ChartDataSet['metric_type'];
  theme_color?: ChartDataSet['theme_color'];
  hex_color?: string;
  getDataLabel?: ChartDataSet['getDataLabel'];
}): ChartDataSet | null => {
  if (!data) {
    return null;
  }
  return {
    ...data,
    ...config
  };
};
const DestinationOverviewMetrics: React.FC<{
  destination_id: string;
}> = t0 => {
  const $ = _c(96);
  const {
    destination_id
  } = t0;
  const breakpoint = useViewportBreakpoint();
  const theme = useTheme();
  const {
    refresh_key,
    updateRefreshKey
  } = useRefreshKey();
  const [date_range, onDateRangeChanged] = useDateRange("last_hour");
  let t1;
  if ($[0] !== destination_id) {
    t1 = {
      filters: {
        destination_id: [destination_id]
      }
    };
    $[0] = destination_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== date_range || $[3] !== t1) {
    t2 = {
      date_range,
      options: t1
    };
    $[2] = date_range;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const metric_configs = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["status"];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== destination_id) {
    t4 = {
      dimensions: t3,
      filters: {
        destination_id: [destination_id]
      }
    };
    $[6] = destination_id;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== date_range || $[9] !== t4) {
    t5 = {
      date_range,
      options: t4
    };
    $[8] = date_range;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const events_status_metric_configs = t5;
  const {
    data: events_status
  } = useMetric("chart:bar", "events_count", refresh_key, events_status_metric_configs);
  const {
    data: successful_attempts
  } = useMetric("chart:bar", "attempts_successful_count", refresh_key, metric_configs);
  const {
    data: failed_attempts
  } = useMetric("chart:bar", "attempts_failed_count", refresh_key, metric_configs);
  const {
    data: average_latency
  } = useMetric("chart:line", "attempts_average_response_time", refresh_key, metric_configs);
  const {
    data: p95_latency
  } = useMetric("chart:line", "attempts_95_response_time", refresh_key, metric_configs);
  const {
    data: p99_latency
  } = useMetric("chart:line", "attempts_99_response_time", refresh_key, metric_configs);
  const {
    data: max_latency
  } = useMetric("chart:line", "attempts_max_response_time", refresh_key, metric_configs);
  const events_status_loading = events_status === undefined;
  let events_status_datasets;
  let t6;
  if ($[11] !== events_status || $[12] !== events_status_loading || $[13] !== theme) {
    let t7;
    bb0: {
      if (events_status_loading) {
        t7 = null;
        break bb0;
      }
      let t8;
      if ($[16] !== events_status || $[17] !== theme) {
        t8 = (config, index) => toDataset(events_status?.[config.value], {
          key: config.value,
          label: config.label,
          sort_order: index,
          metric_type: "count",
          theme_color: "theme_color" in config ? config.theme_color : undefined,
          hex_color: "color" in config ? theme.colors.surface.base[config.color] : undefined,
          getDataLabel: default_data_formatter.count
        });
        $[16] = events_status;
        $[17] = theme;
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      t7 = event_status_configs.map(t8).filter(_temp);
    }
    events_status_datasets = t7;
    bb1: {
      if (!events_status_datasets) {
        t6 = undefined;
        break bb1;
      }
      t6 = events_status_datasets.reduce(_temp2, 0);
    }
    $[11] = events_status;
    $[12] = events_status_loading;
    $[13] = theme;
    $[14] = events_status_datasets;
    $[15] = t6;
  } else {
    events_status_datasets = $[14];
    t6 = $[15];
  }
  const events_status_total = t6;
  const attempts_status_loading = successful_attempts === undefined || failed_attempts === undefined;
  let t7;
  bb2: {
    if (attempts_status_loading) {
      t7 = null;
      break bb2;
    }
    let t8;
    if ($[19] !== successful_attempts) {
      t8 = toDataset(successful_attempts, {
        key: "successful",
        label: "Successful",
        metric_type: "count",
        theme_color: "success",
        getDataLabel: default_data_formatter.count
      });
      $[19] = successful_attempts;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] !== failed_attempts) {
      t9 = toDataset(failed_attempts, {
        key: "failed",
        label: "Failed",
        metric_type: "count",
        theme_color: "danger",
        getDataLabel: default_data_formatter.count
      });
      $[21] = failed_attempts;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] !== t8 || $[24] !== t9) {
      t10 = [t8, t9].filter(_temp3);
      $[23] = t8;
      $[24] = t9;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    t7 = t10;
  }
  const attempts_status_datasets = t7;
  const latency_loading = average_latency === undefined || p95_latency === undefined || p99_latency === undefined || max_latency === undefined;
  let t8;
  bb3: {
    if (latency_loading) {
      t8 = null;
      break bb3;
    }
    let t9;
    if ($[26] !== max_latency || $[27] !== theme.colors.surface.chart.blue_600) {
      t9 = toDataset(max_latency, {
        key: "max",
        label: "Max",
        sort_order: 3,
        metric_type: "average",
        hex_color: theme.colors.surface.chart.blue_600,
        getDataLabel: formatLatency
      });
      $[26] = max_latency;
      $[27] = theme.colors.surface.chart.blue_600;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    let t10;
    if ($[29] !== p99_latency || $[30] !== theme.colors.surface.chart.blue_500) {
      t10 = toDataset(p99_latency, {
        key: "p99",
        label: "99th",
        sort_order: 2,
        metric_type: "average",
        hex_color: theme.colors.surface.chart.blue_500,
        getDataLabel: formatLatency
      });
      $[29] = p99_latency;
      $[30] = theme.colors.surface.chart.blue_500;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    let t11;
    if ($[32] !== p95_latency || $[33] !== theme.colors.surface.chart.blue_400) {
      t11 = toDataset(p95_latency, {
        key: "p95",
        label: "95th",
        sort_order: 1,
        metric_type: "average",
        hex_color: theme.colors.surface.chart.blue_400,
        getDataLabel: formatLatency
      });
      $[32] = p95_latency;
      $[33] = theme.colors.surface.chart.blue_400;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    let t12;
    if ($[35] !== average_latency || $[36] !== theme.colors.surface.chart.blue_300) {
      t12 = toDataset(average_latency, {
        key: "average",
        label: "Avg",
        sort_order: 0,
        metric_type: "average",
        hex_color: theme.colors.surface.chart.blue_300,
        getDataLabel: formatLatency
      });
      $[35] = average_latency;
      $[36] = theme.colors.surface.chart.blue_300;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    let t13;
    if ($[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t9) {
      t13 = [t9, t10, t11, t12].filter(_temp4);
      $[38] = t10;
      $[39] = t11;
      $[40] = t12;
      $[41] = t9;
      $[42] = t13;
    } else {
      t13 = $[42];
    }
    t8 = t13;
  }
  const latency_datasets = t8;
  let t9;
  if ($[43] !== date_range || $[44] !== onDateRangeChanged) {
    t9 = <MetricDatePicker date_range={date_range} onDateRangeChanged={onDateRangeChanged} />;
    $[43] = date_range;
    $[44] = onDateRangeChanged;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  let t10;
  if ($[46] !== updateRefreshKey) {
    t10 = <Button outline={true} icon="reload" onClick={() => updateRefreshKey()}>Refresh</Button>;
    $[46] = updateRefreshKey;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== t10 || $[49] !== t9) {
    t11 = <ViewSplitPaneContentHeader title="Metrics overview" actions={<>{t9}{t10}</>} />;
    $[48] = t10;
    $[49] = t9;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] !== onDateRangeChanged) {
    t12 = range => onDateRangeChanged({
      relative: undefined,
      min: range.min,
      max: range.max
    });
    $[51] = onDateRangeChanged;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      direction: "column",
      gap: 6
    };
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== date_range || $[55] !== destination_id) {
    t14 = stringifyQuery({
      date: getDateRangeQueryParam(date_range),
      destination_id: [destination_id]
    });
    $[54] = date_range;
    $[55] = destination_id;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  const t15 = `/events?${t14}`;
  let t16;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Icon icon="link" pointer={true} right={2} />;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== t15) {
    t17 = <Button ghost={true} muted={true} small={true} to={t15}>Explore{t16}</Button>;
    $[58] = t15;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== events_status_datasets || $[61] !== events_status_loading || $[62] !== events_status_total || $[63] !== t17) {
    t18 = <BaseMetricChart title="Events" chart_type="bar" datasets={events_status_datasets} datasets_type="stacked" force_legend={true} total={events_status_total} loading={events_status_loading} action={t17} />;
    $[60] = events_status_datasets;
    $[61] = events_status_loading;
    $[62] = events_status_total;
    $[63] = t17;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  const t19 = overview_columns_for_breakpoints[breakpoint];
  let t20;
  if ($[65] !== t19) {
    t20 = {
      columns: t19,
      gap: 6
    };
    $[65] = t19;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  let t22;
  if ($[67] !== metric_configs) {
    t21 = <MetricChart metric="events_pending_count" configs={metric_configs} />;
    t22 = <MetricChart metric="events_oldest_pending" configs={metric_configs} />;
    $[67] = metric_configs;
    $[68] = t21;
    $[69] = t22;
  } else {
    t21 = $[68];
    t22 = $[69];
  }
  let t23;
  if ($[70] !== t20 || $[71] !== t21 || $[72] !== t22) {
    t23 = <StyledOverviewMetricsGrid grid={t20}>{t21}{t22}</StyledOverviewMetricsGrid>;
    $[70] = t20;
    $[71] = t21;
    $[72] = t22;
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  const t24 = overview_columns_for_breakpoints[breakpoint];
  let t25;
  if ($[74] !== t24) {
    t25 = {
      columns: t24,
      gap: 6
    };
    $[74] = t24;
    $[75] = t25;
  } else {
    t25 = $[75];
  }
  let t26;
  if ($[76] !== attempts_status_datasets || $[77] !== attempts_status_loading) {
    t26 = <BaseMetricChart title="Delivered attempts" chart_type="bar" datasets={attempts_status_datasets} datasets_type="stacked" force_legend={true} loading={attempts_status_loading} />;
    $[76] = attempts_status_datasets;
    $[77] = attempts_status_loading;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  let t27;
  if ($[79] !== latency_datasets || $[80] !== latency_loading) {
    t27 = <BaseMetricChart title="Response latency" chart_type="line" datasets={latency_datasets} force_legend={true} loading={latency_loading} />;
    $[79] = latency_datasets;
    $[80] = latency_loading;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] !== t25 || $[83] !== t26 || $[84] !== t27) {
    t28 = <StyledOverviewMetricsGrid grid={t25}>{t26}{t27}</StyledOverviewMetricsGrid>;
    $[82] = t25;
    $[83] = t26;
    $[84] = t27;
    $[85] = t28;
  } else {
    t28 = $[85];
  }
  let t29;
  if ($[86] !== t18 || $[87] !== t23 || $[88] !== t28) {
    t29 = <Div flex={t13}>{t18}{t23}{t28}</Div>;
    $[86] = t18;
    $[87] = t23;
    $[88] = t28;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  let t30;
  if ($[90] !== t12 || $[91] !== t29) {
    t30 = <ChartInteractionGroup onTimeRangeSelected={t12}>{t29}</ChartInteractionGroup>;
    $[90] = t12;
    $[91] = t29;
    $[92] = t30;
  } else {
    t30 = $[92];
  }
  let t31;
  if ($[93] !== t11 || $[94] !== t30) {
    t31 = <>{t11}{t30}</>;
    $[93] = t11;
    $[94] = t30;
    $[95] = t31;
  } else {
    t31 = $[95];
  }
  return t31;
};
export const DestinationOverview = t0 => {
  const $ = _c(65);
  const {
    destination
  } = t0;
  const {
    destination_types,
    is_degraded_banner_active
  } = useContext(DashboardContext);
  const destination_type = destination_types?.[destination.type];
  const auth_type = destination.config.auth ? destination.config.auth_type ? (destination_type?.features?.VERIFICATION as DestinationTypeFeatureTypesUnmanaged)?.auth?.[destination.config.auth_type]?.label : destination_type?.label : null;
  const httpConfig = isHttpConfig(destination.config) ? destination.config : null;
  let t1;
  if ($[0] !== destination) {
    t1 = getDestinationDeliveryPolicy(destination);
    $[0] = destination;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const delivery_policy = t1;
  const endpoint = "path" in destination.config ? destination.config.path : "url" in destination.config ? destination.config.url : "https://mock.hookdeck.com";
  const t2 = `/destinations/${destination.id}/settings`;
  let t3;
  if ($[2] !== t2) {
    t3 = <ViewSplitPaneSidebarHeader title="Destination details" action={<Button outline={true} icon="edit" to={t2}>Edit</Button>} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = !!destination?.disabled_at;
  const t5 = !destination?.disabled_at;
  const t6 = !destination?.disabled_at;
  const t7 = !destination?.disabled_at ? "bolt" : "disable";
  const t8 = !destination?.disabled_at ? "Active" : "Disabled";
  let t9;
  if ($[4] !== t4 || $[5] !== t5 || $[6] !== t6 || $[7] !== t7 || $[8] !== t8) {
    t9 = <ViewMetadataItem label="Status"><Badge neutral={t4} subtle={t5} success={t6} icon={t7}>{t8}</Badge></ViewMetadataItem>;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== destination.name) {
    t10 = <ViewMetadataItem label="Name"><ViewMetadataCopyable value={destination.name} wrap={true} /></ViewMetadataItem>;
    $[10] = destination.name;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== destination.description) {
    t11 = destination.description && <ViewMetadataItem label="Description"><ViewMetadataDescription>{destination.description}</ViewMetadataDescription></ViewMetadataItem>;
    $[12] = destination.description;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t10 || $[15] !== t11 || $[16] !== t9) {
    t12 = <ViewSplitPaneSidebarSection>{t9}{t10}{t11}</ViewSplitPaneSidebarSection>;
    $[14] = t10;
    $[15] = t11;
    $[16] = t9;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== destination_type) {
    t13 = <ViewMetadataItem label="Type">{destination_type ? <Badge neutral={true} icon={destination_type.icon as IconName}>{destination_type.label}</Badge> : <Skeleton w={{
        px: 100
      }} h={{
        px: 20
      }} loading={true} />}</ViewMetadataItem>;
    $[18] = destination_type;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = "path" in destination.config ? "Path" : "Endpoint URL";
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      minWidth: 0,
      overflowWrap: "anywhere",
      wordBreak: "break-word"
    };
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== endpoint) {
    t16 = <Text mono={true} title={endpoint} style={t15}>{endpoint}</Text>;
    $[21] = endpoint;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t14 || $[24] !== t16) {
    t17 = <ViewMetadataItem label={t14}>{t16}</ViewMetadataItem>;
    $[23] = t14;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      display: "flex",
      flexWrap: "wrap",
      gap: "4px"
    };
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const t19 = httpConfig?.http_method || "Matching request";
  let t20;
  if ($[27] !== t19) {
    t20 = <ViewMetadataItem label="HTTP method"><Div style={t18}><Badge neutral={true} subtle={true}>{t19}</Badge></Div></ViewMetadataItem>;
    $[27] = t19;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const t21 = delivery_policy?.rate && delivery_policy?.period ? `${delivery_policy.rate} / ${delivery_policy.period}` : "None";
  let t22;
  if ($[29] !== t21) {
    t22 = <ViewMetadataItem label="Max delivery rate"><Badge neutral={true} subtle={true}>{t21}</Badge></ViewMetadataItem>;
    $[29] = t21;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  let t23;
  if ($[31] !== auth_type) {
    t23 = <ViewMetadataItem label="Authentication method">{auth_type ? <Badge neutral={true} subtle={true}>{formatMetadataLabel(auth_type)}</Badge> : <Badge neutral={true} subtle={true}>No auth</Badge>}</ViewMetadataItem>;
    $[31] = auth_type;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  const t24 = !destination.config?.path_forwarding_disabled;
  const t25 = !!destination.config?.path_forwarding_disabled;
  const t26 = !destination.config?.path_forwarding_disabled ? "Enabled" : "Disabled";
  let t27;
  if ($[33] !== t24 || $[34] !== t25 || $[35] !== t26) {
    t27 = <ViewMetadataItem label="Path forwarding"><Badge success={t24} neutral={t25} subtle={true}>{t26}</Badge></ViewMetadataItem>;
    $[33] = t24;
    $[34] = t25;
    $[35] = t26;
    $[36] = t27;
  } else {
    t27 = $[36];
  }
  let t28;
  if ($[37] !== t13 || $[38] !== t17 || $[39] !== t20 || $[40] !== t22 || $[41] !== t23 || $[42] !== t27) {
    t28 = <ViewSplitPaneSidebarSection title="Configuration" bordered={true}>{t13}{t17}{t20}{t22}{t23}{t27}</ViewSplitPaneSidebarSection>;
    $[37] = t13;
    $[38] = t17;
    $[39] = t20;
    $[40] = t22;
    $[41] = t23;
    $[42] = t27;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== destination.id) {
    t29 = <ViewMetadataItem label="Destination ID"><ViewMetadataCopyable value={destination.id} /></ViewMetadataItem>;
    $[44] = destination.id;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  let t30;
  if ($[46] !== destination.created_at) {
    t30 = <ViewMetadataItem label="Created at"><ViewMetadataDate date={destination.created_at} /></ViewMetadataItem>;
    $[46] = destination.created_at;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] !== destination.updated_at) {
    t31 = <ViewMetadataItem label="Last updated"><ViewMetadataDate date={destination.updated_at} /></ViewMetadataItem>;
    $[48] = destination.updated_at;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[50] !== t29 || $[51] !== t30 || $[52] !== t31) {
    t32 = <ViewSplitPaneSidebarSection title="Metadata" bordered={true}>{t29}{t30}{t31}</ViewSplitPaneSidebarSection>;
    $[50] = t29;
    $[51] = t30;
    $[52] = t31;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  let t33;
  if ($[54] !== t12 || $[55] !== t28 || $[56] !== t3 || $[57] !== t32) {
    t33 = <StyledViewSplitPaneSidebar><StyledViewSplitPaneSidebarBody>{t3}{t12}{t28}{t32}</StyledViewSplitPaneSidebarBody></StyledViewSplitPaneSidebar>;
    $[54] = t12;
    $[55] = t28;
    $[56] = t3;
    $[57] = t32;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] !== destination.id) {
    t34 = <StyledViewSplitPaneContent><DestinationOverviewMetrics destination_id={destination.id} /></StyledViewSplitPaneContent>;
    $[59] = destination.id;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== is_degraded_banner_active || $[62] !== t33 || $[63] !== t34) {
    t35 = <StyledViewSplitPaneLayout banner_active={is_degraded_banner_active}>{t33}{t34}</StyledViewSplitPaneLayout>;
    $[61] = is_degraded_banner_active;
    $[62] = t33;
    $[63] = t34;
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  return t35;
};
function _temp(dataset) {
  return !!dataset;
}
function _temp2(total, dataset_0) {
  return total + dataset_0.total;
}
function _temp3(dataset_1) {
  return !!dataset_1;
}
function _temp4(dataset_2) {
  return !!dataset_2;
}